/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  IonContent,
  IonPage,
  IonToast,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  useIonRouter,
} from "@ionic/react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import "./Home.css";
import useAuth from "../hooks/useAuth";
import ArcOnboarding from "../components/onboarding/Onboarding";
import { Preferences } from "@capacitor/preferences";
import useHaptics from "../hooks/useHaptics";
import GoalsSection from "../components/home/GoalsSection";
import ConnectionCardsSection from "../components/home/ConnectionCardsSection";
import {
  ConnectionCardData,
  HomeTherapyData,
  TherapyDataEntry,
} from "../types/homeTypes";
import { getUserDevices } from "../services/device";
import { getWeeklyStatus } from "../services/dashboard";
import { useStorage } from "../contexts/StorageContext";
import { ionicStorage, getIonicStorage } from "../services/storageService";
import {
  acceptFollowRequest,
  getAllFollowing,
  pendingRequestsActionRequired,
  rejectFollowRequest,
} from "../services/datashare";
import { ActionsRequiredSection } from "../components/home/ActionsRequiredItems";
import {
  useNotifications,
  useSocket,
  useSocketConnection,
} from "../contexts/SocketContext";
import DeviceScanModal from "../components/home/DeviceScanModal";
import { FollowingList } from "./settings/datasharing/DataSharingTypes";
import {
  containerVariants,
  detectDeviceType,
  getIsoDate,
  getUserName,
  itemVariants,
  mapFollowers,
} from "../utils/helpers";
import AllFollowingList from "../components/ui/popups/AllFollowingList";
import { Following } from "./settings/datasharing/DataSharingTypes";
import { HealthPreferencesStorage } from "./settings/MyHealthData";
import { useHistory } from "react-router";
import BiWazeTherapyCard from "../components/therapy/BiWazeTherapyCard";
import { getDateRange, getTodayFormatted } from "../utils/date";
import EnvironmentalDataManager from "../components/home/EnvironmentalDataManager";
import { insightsMockData } from "../utils/guestHome";
import { useToaster } from "../components/ui/toastbar/Toaster";
import {
  reAssignNotifications,
  retrieveNotificationsSettings,
} from "../services/user";
import { InvitedNotification } from "./dashboard/subcomponents/InvitedNotification";
import InsightsGraphVisx from "../components/home/InsightsGraphVisx";
import { HeaderNotificationItem } from "../components/ui/HeaderNotify";
import { useMyOrgRelationship } from "./settings/datasharing/useMyOrgRelationship";
import {
  acceptNeedsConfirmation,
  handleOrgInvitationAction,
  invitationActionItem,
  replacementWarning,
} from "../services/orgRelationship";
import AcceptInvitationConfirm from "./settings/datasharing/AcceptInvitationConfirm";

const HomeDashboard: React.FC = () => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const { user, isNewUser, getHealthData, isNativePlatform, platFormType } =
    useAuth();

  // Helper to cancel previous requests and create new controller
  const cancelPreviousRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  };

  const [activityAdherence, setActivityAdherence] = useState<HomeTherapyData>({
    // therapies: [],
    therapyData: [],
    insights: {
      therapies: [],
      vitals: {
        spo2: [],
        heart_rate: [],
      },
    },
    date: "",
    showMockData: false,
    devices: [],
  });
  const [filteredTherapies, setFilteredTherapies] = useState<any[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<any>({
    label: "Me",
    value: user?.profile_id || "",
  });

  const [connectionCards, setConnectionCards] = useState<ConnectionCardData[]>([
    {
      id: "BiWaze® Cough",
      title: `Link to BiWaze® System`,
      description: `Make sense of your therapy by linking to your BiWaze® Cough device.`,
      buttonText: "Scan your device",
    },
  ]);
  const [goalsRefreshKey, setGoalsRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(true);
  const formattedDate = getTodayFormatted();
  const { successActivity } = useHaptics();
  const router = useIonRouter();
  const { storage, isStorageReady } = useStorage();
  const [actionsRequiredItems, setActionsRequiredItems] = useState<any[]>([]);

  // ───────────────────────────────────────────────────────────────────────────────────────────
  // B2-4 Task B — "the doorbell". An unanswered ORG invitation must be visible here, not only
  // buried in Settings → Data Sharing.
  //
  // ⭐ It is DERIVED, never merged into `actionsRequiredItems`. That state is blaze's list and
  // is overwritten wholesale by `getNotifications()` and rewritten by the Accept handler's
  // `filter`, so an injected org item would vanish on the next refresh or the next accept of an
  // unrelated follow request. Deriving also keeps this to ONE source of truth per surface.
  //
  // ⚠️ No push, no socket, NO POLLING: the invitation appears at the next `GET /me` — the same
  // read the Settings card does, through the same hook, so both surfaces agree by construction.
  // ───────────────────────────────────────────────────────────────────────────────────────────
  const orgRelationship = useMyOrgRelationship();
  const orgInvitationItem = useMemo(
    () => invitationActionItem(orgRelationship.invitation),
    [orgRelationship.invitation]
  );
  // R-32: the tapped invitation item, held only while its accept-time disclosure is open. The
  // dashboard asks the SAME predicate the Data Sharing card asks (`acceptNeedsConfirmation`
  // wraps it), so one invitation is never described two different ways.
  const [confirmingAccept, setConfirmingAccept] = useState<any>(null);
  const actionItems = useMemo(
    () =>
      orgInvitationItem ? [orgInvitationItem, ...actionsRequiredItems] : actionsRequiredItems,
    [orgInvitationItem, actionsRequiredItems]
  );
  const { notifications, requestNotifications, dailyNotification } =
    useNotifications();
  const { initializeUser, currentUser, headerNotification } =
    useSocketConnection();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [connectedDeviceData, setConnectedDeviceData] = useState<any>(null);
  const [onFollowingModalOpen, setOnFollowingModalOpen] = useState(false);
  const [isDeviceScanModalOpen, setIsDeviceScanModalOpen] = useState(false);
  const dateRange = getDateRange();
  const history = useHistory();
  const [following, setFollowing] = useState<any[]>([]);
  const toastbar = useToaster();
  const followingList: Following[] = React.useMemo(() => {
    return mapFollowers(following);
  }, [following]);
  const [FCMToken, setFCMToken] = useState<string | null>(null);
  const [timeOutDailyReminder, setTimeOutDailyRemainder] = useState(false);
  const [notificationsHeader, setNotificationHeader] = useState<any[]>([]);

  useEffect(() => {
    const fetchFCMToken = async () => {
      const { value } = await Preferences.get({ key: "fcmToken" });
      setFCMToken(value ?? null);
    };
    fetchFCMToken();
  }, [user?.profile_id]);

  useEffect(() => {
    fetchFollowingData();
  }, [storage, isStorageReady]);
  const fetchFollowingData = async () => {
    try {
      if (!storage || !isStorageReady) return;

      const storedFollowing = await getIonicStorage(storage, "following");
      if (storedFollowing?.length) {
        setFollowing(storedFollowing);
      }

      const allFollowing: FollowingList[] = await getAllFollowing();
      await ionicStorage(storage, "following", allFollowing);

      setFollowing(allFollowing);
    } catch (error) {
      console.error("Error fetching following data:", error);
    }
  };
  useEffect(() => {
    if (user && user.profile_id && !currentUser) {
      initializeUser(user);
    }
    // toastbar.success("Welcome back to Arc Health!", 3000, "bottom");
    requestNotifications();
  }, [user, currentUser, initializeUser]);

  const saveDualData = async (key: string, value: any) => {
    try {
      await Preferences.set({
        key,
        value: JSON.stringify(value),
      });

      if (storage && isStorageReady) {
        await ionicStorage(storage, key, value);
      }
    } catch (error) {
      console.error(`Error saving dual data for key ${key}:`, error);
    }
  };

  const loadConnectedDevice = async (profile_id?: number) => {
    try {
      const profileIdToUse =
        profile_id || selectedProfile?.value || user?.profile_id;

      // --- Fetch devices for current user first ---
      const currentUserDevices = user?.profile_id
        ? await getUserDevices(user.profile_id)
        : [];

      if (currentUserDevices && currentUserDevices.length > 0) {
        setConnectedDeviceData(currentUserDevices[0]);
      } else if (storage && isStorageReady) {
        // Fallback: check local storage if no devices found for current user
        const storageData = await getIonicStorage(storage, "myDevices");
        if (storageData && storageData.length > 0) {
          setConnectedDeviceData(storageData);
        } else {
          setConnectedDeviceData(null);
        }
      }

      // --- If a different profile is selected, fetch their devices as well ---
      if (profileIdToUse && profileIdToUse !== user?.profile_id) {
        const otherProfileDevices = await getUserDevices(profileIdToUse);
        if (otherProfileDevices && otherProfileDevices.length > 0) {
          setConnectedDeviceData(otherProfileDevices[0]);
        } else {
          setConnectedDeviceData(null);
          setConnectionCards([
            {
              id: "biwaze_cough",
              adherence_type: "biwaze_cough",
              title: `${selectedProfile.label} is not Connected to BiWaze® Cough`,
              description: `${selectedProfile.label} has not linked a BiWaze® Cough device yet.`,
              buttonText: "",
              className: "biwaze-cough",
            },
          ]);
        }
      }
    } catch (error) {
      console.error("Error loading connected device:", error);
      setConnectedDeviceData(null);
    }
  };

  const loadData = async (key: string) => {
    try {
      if (storage && isStorageReady) {
        const storageData = await getIonicStorage(storage, key);
        if (storageData) {
          return storageData;
        }
      }

      const preferencesData = await Preferences.get({ key });
      if (preferencesData.value) {
        const parsedData = JSON.parse(preferencesData.value);

        if (storage && isStorageReady) {
          await ionicStorage(storage, key, parsedData);
        }

        return parsedData;
      }

      return null;
    } catch (error) {
      console.error(`Error loading data for key ${key}:`, error);
      return null;
    }
  };
  const isReassigningNotifications = useRef(false);

  const fetchDashboardNotifications = async () => {
    try {
      const notifications: HeaderNotificationItem[] = headerNotification?.data;
      if (!Array.isArray(notifications)) return;

      const { value: closedDate } = await Preferences.get({
        key: "dismissedAlertDate",
      });
      const today = getIsoDate(new Date());
      const isMissedAlertClosedForToday = notifications.some(
        (n: any) =>
          n.type === "MISSED_DAYS_ALERT" &&
          closedDate === today &&
          n.lastNotificationAt &&
          n.lastNotificationAt === today
      );
      if (isMissedAlertClosedForToday) {
        dailyNotification(true);
      }
      // ✅ Prevent concurrent reassignment
      if (isReassigningNotifications.current) {
        console.log("reAssignNotifications already running. Skipping.");
        return;
      }
      isReassigningNotifications.current = true;
      try {
        await reAssignNotifications(notifications);
      } finally {
        isReassigningNotifications.current = false;
      }

      /* -------------------------------
       DAILY REMINDER
    -------------------------------- */
      const dailyReminder = notifications.find(
        (n: any) => n.type === "DAILY_REMINDER"
      );

      if (dailyReminder?.schedule?.time) {
        const now = new Date();
        const [hh, mm, ss = 0] = dailyReminder.schedule.time
          .split(":")
          .map(Number);

        const reminderTime = new Date();
        reminderTime.setHours(hh, mm, ss, 0);

        const FIVE_MINUTES_MS = 5 * 60 * 1000;
        const diffMs = Math.abs(now.getTime() - reminderTime.getTime());

        setTimeOutDailyRemainder(diffMs <= FIVE_MINUTES_MS);
      } else {
        setTimeOutDailyRemainder(false);
      }

      /* -------------------------------
       STORE REMAINING NOTIFICATIONS
    -------------------------------- */

      setNotificationHeader(notifications);
    } catch (error) {
      console.error("Error fetching notification data:", error);
    }
  };

  useEffect(() => {
    const sendNotifications = async () => {
      await fetchDashboardNotifications();
    };
    sendNotifications();
  }, [headerNotification]);

  useEffect(() => {
    const loadSavedStatus = async () => {
      if (!isStorageReady) return;

      try {
        const savedStatus = await loadData("dailyStatus");
        if (savedStatus) {
          setActivityAdherence(savedStatus);
        }
      } catch (error) {
        console.error("Error loading saved status:", error);
      }
    };

    loadSavedStatus();
  }, [isStorageReady, selectedProfile]);

  const fetchDailyStatus = async (profile_id?: number) => {
    const controller = cancelPreviousRequests();
    const profileIdToUse =
      profile_id || selectedProfile?.value || user?.profile_id;

    if (profileIdToUse) {
      try {
        const response: HomeTherapyData = await getWeeklyStatus(
          profileIdToUse as number,
          {
            date: formattedDate,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }
        );

        if (!controller.signal.aborted) {
          if (profileIdToUse === user?.profile_id) {
            await saveDualData("dailyStatus", response);
          }
          setActivityAdherence(response);
          setLoading(false);
        }
      } catch (error) {
        if ((error as any)?.name !== "AbortError") {
          console.error("Error fetching daily status:", error);
        }
      }
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        const profileIdToUse = selectedProfile?.value || user?.profile_id;

        if (profileIdToUse) {
          setLoading(true);
          await fetchDailyStatus(profileIdToUse as number);
        }
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    if (selectedProfile?.value || user?.profile_id) {
      initializeApp();
      loadConnectedDevice();
    }
  }, [selectedProfile?.value, user?.profile_id]);

  // useEffect(() => {
  //   const fetchConnectedDevices = async () => {
  //     const profileIdToUse = selectedProfile?.value || user?.profile_id;
  //     if (profileIdToUse) {
  //       const ConnectedDevices = await getUserDevices(profileIdToUse as number);

  //       if (profileIdToUse === user?.profile_id && storage && isStorageReady) {
  //         await ionicStorage(storage, "myDevices", ConnectedDevices);
  //       }
  //     }
  //   };
  //   fetchConnectedDevices();
  // }, [selectedProfile?.value]);

  useEffect(() => {
    if (activityAdherence?.therapyData?.length > 0) {
      if (
        selectedProfile?.value === user?.profile_id ||
        !selectedProfile?.value
      ) {
        const unpaired = activityAdherence.therapyData
          .filter((therapy: TherapyDataEntry) => !therapy.hasPairedDevice)
          .map((therapy: TherapyDataEntry) => ({
            id: therapy.name,
            title: "Link to BiWaze® System",
            description: `Make sense of your therapy by linking to your BiWaze® Therapy.`,
            buttonText: "Scan your device",
          }));
        setConnectionCards([unpaired[0]]);
      } else {
        const unpaired = activityAdherence.therapyData
          .filter((therapy: TherapyDataEntry) => !therapy.hasPairedDevice)
          .map((therapy: TherapyDataEntry) => ({
            id: therapy.name,
            title: `Link to ${therapy.name || "therapy device"}`,
            description: `${selectedProfile?.label} is not linked to ${therapy.name || "therapy device"
              }.`,
            buttonText: "",
          }));
        setConnectionCards(unpaired);
      }

      const paired = activityAdherence.therapyData.filter(
        (therapy: TherapyDataEntry) =>
          therapy.hasPairedDevice
      );
      setFilteredTherapies(paired);
    } else {
      setConnectionCards([]);
      setFilteredTherapies([]);
    }

    // const profileIdToUse = selectedProfile?.value || user?.profile_id;
    // if (profileIdToUse) {
    //   getUserDevices(profileIdToUse as number).then(() => {
    //     return true;
    //   });
    // }
  }, [
    activityAdherence,
    selectedProfile?.value,
    user?.profile_id,
    connectedDeviceData,
  ]);

  const fetchPreferences = async () => {
    const deviceType = detectDeviceType();
    try {
      await HealthPreferencesStorage.loadUserPreferencesFromService(deviceType);
    } catch (error) {
      console.error("Error fetching user preferences:", error);
    }
  };
  const fetchAQI = async () => {
    await EnvironmentalDataManager.fetchWeatherData(user, storage);
  };
  const fetchLoadAQI = async () => {
    await EnvironmentalDataManager.loadWeatherData();
  };
  useEffect(() => {
    fetchPreferences();
    if (isNativePlatform) {
      fetchAQI();
      // fetchLoadAQI();
    }
    checkHealthKit();
    getNotifications();
  }, []);

  const { socket, dataSharingRefresh } = useSocket();

  useEffect(() => {
    if (socket) {
      getNotifications();
    }
  }, [dataSharingRefresh]);

  useEffect(() => {
    if (socket) {
      socket.on("activity_refresh", () => {
        // Only refresh when viewing own data, not another user's profile
        const isViewingOwnProfile =
          !selectedProfile?.value ||
          selectedProfile?.value === user?.profile_id;
        if (isViewingOwnProfile) {
          fetchDailyStatus();
        }
      });
      socket.on("dataSharing_refresh", () => {
        fetchFollowingData();
      });
    }
    return () => {
      if (socket) {
        socket.off("activity_refresh");
      }
    };
  }, [socket, selectedProfile?.value, user?.profile_id]);

  const checkHealthKit = async () => {
    await getHealthData();
  };

  useEffect(() => {
    const checkOnboardingStep = async () => {
      const { value: skippedValue } = await Preferences.get({
        key: "onboardingSkipped",
      });
      const skipped = skippedValue === "true";
      if (!skipped && isNewUser) {
        setShowOnboarding(true);
      }
    };
    checkOnboardingStep();
  }, [isNewUser]);

  const getNotifications = async () => {
    try {
      const response = await pendingRequestsActionRequired();

      if (response && Array.isArray(response)) {
        await ionicStorage(storage, "actionsRequired", response);
        setActionsRequiredItems(response);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleConnectDevice = () => {
    setIsDeviceScanModalOpen(true);
  };

  const handleDeviceAdded = async () => {
    try {
      const profileIdToUse = selectedProfile?.value || user?.profile_id;
      await fetchDailyStatus(profileIdToUse as number);
      successActivity();
    } catch (error) {
      console.error("Error refreshing data after device addition:", error);
      setToastMessage(
        "Device connected, but failed to refresh data. Please pull to refresh."
      );
      setShowToast(true);
    }
  };
  const [isMockData, setIsMockData] = useState(false);
  const [InsightsData, setInsightsData] = useState<any>(null);
  useEffect(() => {
    if (!activityAdherence?.showMockData) {
      setInsightsData(activityAdherence?.insights);
      setIsMockData(false);
    } else {
      setInsightsData(insightsMockData);
      setIsMockData(true);
    }
  }, [activityAdherence]);

  const handleProfileChange = (selected: { value: number; label: string }) => {
    // Cancel any in-flight requests before switching
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setSelectedProfile(selected);
    console.log("Profile changed to:", selected);
    // fetchDailyStatus(selected.value as number);
    loadConnectedDevice(selected.value as number);
  };

  /**
   * Accept/Decline for an ORG invitation card (B2-4 Task B).
   *
   * ⛔ Same hook, same route, same reload as the Data Sharing card — never a second
   * `acceptOrgInvitation()` wired straight from here. Returns true when the tap belonged to an
   * invitation, so the blaze handlers below can stay untouched.
   *
   * The hook never throws (it catches and returns false), and this screen does not render the
   * hook's toast, so the outcome is reported here instead — otherwise a failed accept would
   * still show "Request accepted".
   */
  const handledOrgInvitation = async (
    item: any,
    verb: "accept" | "decline"
  ): Promise<boolean> => {
    const outcome = await handleOrgInvitationAction(item, verb, orgRelationship);
    if (outcome === null) return false;
    if (outcome) {
      toastbar.success(verb === "accept" ? "Connected" : "Invitation declined", 3000, "bottom");
    } else {
      setToastMessage(
        verb === "accept"
          ? "Could not accept. Please try again."
          : "Could not decline. Please try again."
      );
      setShowToast(true);
    }
    return true;
  };

  const confirmAcceptRequest = async (selectedRequest?: any): Promise<void> => {
    if (selectedRequest.type === "incoming_follower_request") {
      await acceptFollowRequest({ relationship_id: selectedRequest.id });
    } else if (selectedRequest.type === "incoming_following_request") {
      await acceptFollowRequest({ relationship_id: selectedRequest.id });
    }
    getNotifications();
  };

  const confirmRejectRequest = async (selectedRequest?: any): Promise<void> => {
    if (!selectedRequest) return;
    if (selectedRequest.type === "incoming_follower_request") {
      await rejectFollowRequest({ relationship_id: selectedRequest.id });
    } else if (selectedRequest.type === "incoming_following_request") {
      await rejectFollowRequest({ relationship_id: selectedRequest.id });
    }
    getNotifications();
  };
  const renderInsightsSection = () => (
    <>
      {activityAdherence?.insights && (
        <InsightsGraphVisx
          profileId={Number(selectedProfile?.value ?? user?.profile_id)}
          insightsData={InsightsData}
          dateRange={{
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }}
          isCurrentUser={
            selectedProfile?.value === user?.profile_id || !selectedProfile
          }
          isMockData={isMockData}
          onClick={handleNavigateToDetails}
        />
      )}
      {/* <InsightsGraph
        profileId={Number(selectedProfile?.value ?? user?.profile_id)}
        insightsData={InsightsData}
        dateRange={{
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }}
        isCurrentUser={
          selectedProfile?.value === user?.profile_id || !selectedProfile
        }
        isMockData={isMockData}
        onClick={handleNavigateToDetails}
      /> */}
    </>
  );
  const renderActionsRequiredSection = () => (
    <div>
      {(selectedProfile?.value === user?.profile_id ||
        !selectedProfile?.value) && (
          <ActionsRequiredSection
            actionsRequiredItems={actionItems}
            actionButtons={[
              {
                isPrimary: true,
                text: "Accept",
                action: async (item: any) => {
                  // ⛔ Disclose BEFORE acting when accepting would end another organization's
                  // access — and only then. False for every blaze request and for every
                  // patient with no incumbent org, so those stay a single direct tap.
                  if (acceptNeedsConfirmation(item, orgRelationship)) {
                    setConfirmingAccept(item);
                    return;
                  }
                  // ⛔ Return, do not fall through: the blaze lines below toast success
                  // unconditionally, and an org accept can fail without throwing.
                  if (await handledOrgInvitation(item, "accept")) return;
                  await confirmAcceptRequest(item);
                  setActionsRequiredItems(
                    actionsRequiredItems.filter((i) => i.id !== item.id)
                  );
                  toastbar.success("Request accepted", 3000, "bottom");
                },
              },
              {
                text: "Decline",
                action: async (item: any) => {
                  if (await handledOrgInvitation(item, "decline")) return;
                  setToastMessage("Action declined");
                  await confirmRejectRequest(item);
                  setShowToast(true);
                  toastbar.success("Request declined", 3000, "bottom");
                },
                variant: "outline",
              },
            ]}
          />
        )}
    </div>
  );

  const handleNavigateToDetails = (deviceType = "cough") => {
    if (deviceType !== "cough" && deviceType !== "clear") {
      deviceType = "cough";
    }
    const profileName = selectedProfile?.label ?? user?.name;
    history.push(
      `/activity/details/${deviceType}?profile_id=${selectedProfile?.value ?? user?.profile_id
      }&name=${encodeURIComponent(profileName)}&deviceType=${deviceType}`
    );
  };

  const renderConnectionCardsSection = () =>
    selectedProfile?.value === user?.profile_id ? (
      <ConnectionCardsSection
        connectionCards={connectionCards}
        handleConnectDevice={handleConnectDevice}
      />
    ) : filteredTherapies.length === 0 ? (
      <BiWazeTherapyCard
        title="Therapies"
        currentMonth={new Date().toLocaleString("default", {
          month: "long",
        })}
        todaysData={true}
        therapies={
          activityAdherence?.therapyData.filter(
            (therapy: any) =>
              therapy.hasPairedDevice
          ) as any[]
        }
        onCardClick={handleNavigateToDetails}
        oldestKnownTherapyDate={activityAdherence?.oldestKnownTherapyDate}
      />
    ) : (
      <></>
    );

  const renderTherapyWeeklySummarySection = () => (
    <>
      {activityAdherence?.therapyData.length > 0 && (
        <BiWazeTherapyCard
          key={`therapy-card-${selectedProfile?.value ?? user?.profile_id
            }-${activityAdherence?.therapyData.map((t) => t.name).join("-")}-${activityAdherence?.date
            }`}
          title={"Therapies"}
          currentMonth={new Date().toLocaleString("default", {
            month: "long",
          })}
          todaysData
          therapies={
            activityAdherence?.therapyData.filter(
              (therapy: any) =>
                therapy.hasPairedDevice
            ) as any[]
          }
          onCardClick={handleNavigateToDetails}
          oldestKnownTherapyDate={activityAdherence?.oldestKnownTherapyDate}
        />
      )}
    </>
  );

  const renderGoalsSection = () => (
    <GoalsSection
      key={`goals-${selectedProfile?.value ?? user?.profile_id
        }-${goalsRefreshKey}`}
      profile_id={Number(selectedProfile?.value || user?.profile_id)}
      onGoalUpdate={() => {
        toastbar.success("Added Record", 3000, "bottom");
        fetchDailyStatus();
      }}
    />
  );

  const componentMap: Record<string, () => React.ReactElement> = {
    InsightsSection: renderInsightsSection, // NEW
    TherapyActivitiesSection: renderTherapyWeeklySummarySection,
    ActionsRequiredSection: renderActionsRequiredSection,
    ConnectionCardsSection: renderConnectionCardsSection,
    GoalsSection: renderGoalsSection,
  };

  const homeLayoutConfig = useMemo(
    () => [
      {
        key: "ActionsRequiredSection",
        // ⚠️ `actionItems`, not `actionsRequiredItems` — an org invitation is the only item for
        // a patient with no follow requests, and gating on blaze's list alone would hide the
        // section that holds it.
        showIf: () =>
          actionItems.length > 0 &&
          actionItems.some((item) => item.status === "pending") &&
          (selectedProfile?.value === user?.profile_id ||
            !selectedProfile?.value),
      },
      {
        key: "InsightsSection",
        showIf: () => activityAdherence?.insights?.therapies?.length > 0,
      },
      {
        key: "TherapyActivitiesSection",
        showIf: () => filteredTherapies?.length > 0,
      },
      {
        key: "ConnectionCardsSection",
        showIf: () =>
          selectedProfile?.value === user?.profile_id
            ? filteredTherapies?.length === 0
            : true,
      },
      {
        key: "GoalsSection",
        showIf: () => true,
      },
    ],
    [
      actionItems,
      connectionCards,
      connectedDeviceData,
      filteredTherapies,
      selectedProfile?.value,
      goalsRefreshKey,
      // user?.profile_id,
    ]
  );

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    // Cancel any in-flight requests before refreshing
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!activityAdherence) setContentLoaded(false);
    checkHealthKit();
    try {
      setSelectedProfile({
        label: "Me",
        value: user?.profile_id || "",
      });
      const profileIdToUse = selectedProfile?.value || user?.profile_id;
      if (profileIdToUse) {
        const response = await getWeeklyStatus(profileIdToUse as number, {
          date: formattedDate,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });
        setActivityAdherence(response);
        getNotifications();
      }
      setTimeout(() => setContentLoaded(true), 300);
    } catch (error) {
      console.error("Error refreshing data:", error);
      setToastMessage("Couldn't refresh data. Please try again");
      setShowToast(true);
    } finally {
      if (event && typeof event.detail?.complete === "function") {
        event.detail.complete();
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <IonPage>
      <IonContent className="main-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingText="Pull to refresh"
            refreshingSpinner="circles"
            refreshingText="Refreshing..."
          ></IonRefresherContent>
        </IonRefresher>

        <Header
          showNotification={notifications.length > 0}
          onNotificationClick={() => router.push("/notifications")}
          onDropDownChange={handleProfileChange}
          selectedDropDown={selectedProfile}
          showGreeting={true}
          following={following}
          onOpenFollowingModal={() => setOnFollowingModalOpen(true)}
          showUserName={true}
          closeAlert={(close) => {
            dailyNotification(close);
          }}
          showReminders={
            selectedProfile?.value === user?.profile_id
              ? notificationsHeader
              : []
          }
          userName={getUserName(user)}
          timeOutDailyReminder={timeOutDailyReminder}
          avatarUrl={user?.avatar}
          showAvatar={!(following && following.length > 0)}
          loading={loading}
          pageType="home"
          showOfflineStatus={true}
          hasActionsRequired={
            actionItems.length > 0 &&
            actionItems.some((item) => item.status === "pending") &&
            (selectedProfile?.value === user?.profile_id || !selectedProfile?.value)
          }
        />

        <div
          className="home-container"
          style={{
            minHeight: "100%",
            height: "auto",
            overflow: "visible",
          }}
        >
          <motion.div
            className="cols-1 lg:grid-cols-2 lg:gap-4 lg:p-4 w-full"
            variants={containerVariants}
            initial="hidden"
            animate={contentLoaded ? "visible" : "hidden"}
            style={{
              minHeight: "100%",
              height: "auto",
              paddingBottom: "20px",
            }}
            transition={{
              type: "spring",
              stiffness: 80,
              damping: 18,
              duration: 0.8,
            }}
          >
            {homeLayoutConfig.map((item) => {
              // Add flex column for InsightsSection, ConnectionCardsSection, TherapyActivitiesSection
              const isFlexCol = [
                "InsightsSection",
                "ConnectionCardsSection",
                "TherapyActivitiesSection",
              ].includes(item.key);

              return (
                item.showIf() && (
                  <motion.div
                    key={item.key}
                    variants={itemVariants}
                    style={{
                      display: isFlexCol ? "flex" : undefined,
                      flexDirection: isFlexCol ? "column" : undefined,
                    }}
                  >
                    {componentMap[item.key]()}
                  </motion.div>
                )
              );
            })}
          </motion.div>

          {onFollowingModalOpen && (
            <AllFollowingList
              isOpen={onFollowingModalOpen}
              following={followingList}
              onSelect={handleProfileChange}
              onClose={() => setOnFollowingModalOpen(false)}
            />
          )}
          {isDeviceScanModalOpen && (
            <DeviceScanModal
              isOpen={isDeviceScanModalOpen}
              onClose={() => {
                setIsDeviceScanModalOpen(false);
                handleDeviceAdded();
              }}
            />
          )}
          {/* Invitation Modal */}
          {user && (
            <InvitedNotification
              toastbar={toastbar as any}
              user={user as any}
              onProfileUpdate={
                // Fetch actions required and following list after accepting invitation
                async () => {
                  getNotifications();
                  fetchFollowingData();
                }
              }
            />
          )}

          {showOnboarding && (
            <ArcOnboarding
              refresh={async () => {
                setGoalsRefreshKey((prev) => prev + 1);
                await fetchDailyStatus();
                setSelectedProfile(selectedProfile);
                handleDeviceAdded();
                console.log("Onboarding refresh complete");
              }}
            />
          )}

          {/* ⛔ The invitation card's own sentence stays on the list behind this — the
              disclosure is appended to the flow, never substituted for it. */}
          <AcceptInvitationConfirm
            warning={confirmingAccept ? replacementWarning(orgRelationship) : ""}
            busy={
              !!confirmingAccept && orgRelationship.busyOrgId === confirmingAccept.org_id
            }
            onConfirm={async () => {
              const item = confirmingAccept;
              setConfirmingAccept(null);
              if (item) await handledOrgInvitation(item, "accept");
            }}
            onCancel={() => setConfirmingAccept(null)}
          />

          <div className="footer-space"></div>

          <IonToast
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            message={toastMessage}
            duration={1500}
            position="top"
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default HomeDashboard;
